import * as React from "react";
import { connect } from "react-redux";
import { validators } from "not-valid";

import { OldProgressBarQuestionnaireSectionUnconnected } from "@teamprevent/common/questionnaires/components/OldProgressBarQuestionnaireSection";
import { getQuestionConfig, CustomQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { makeMapStateToQuestionnaireSectionProps, mapDispatchToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { checkExistenceOfQuestionWithId } from "@teamprevent/common/questionnaires/checkExistenceOfQuestionWithId";

import * as questionIds from "../questions/questionIds";
import { HealthSurveillanceSectionProps } from "../models";
import { EmployeeRecord, LinkObject } from "../../model";
import { GENDER_OPTIONS } from "../../utils";
import { sections } from "../questionnaire";

// eslint-disable-next-line max-len
const HealthSurveillancePersonalDetailsSectionUnconnected: React.FunctionComponent<HealthSurveillanceSectionProps & { getOrgLevels: () => Promise<LinkObject[]>; autoFill?: boolean; employeeRecord?: EmployeeRecord }> = (props) => {
    const [orgLevels, setOrgLevels] = React.useState<LinkObject[]>([]);

    React.useEffect(() => {
        (async () => {
            if (!props.readonly) {
                const response = await props.getOrgLevels();
                setOrgLevels(response);
            }
        })();
    }, []);

    const onOrgLevelChange = (orgLevelId: string) => {
        props.updateAnswer({
            sectionId: sections.PersonalDetails.id,
            id: questionIds.OrgLevelName,
            question: {
                id: questionIds.OrgLevelId,
                isValid: true,
                question: (getQuestionConfig(props.questionConfigs, questionIds.OrgLevelId) as CustomQuestionConfig).questionText,
                answer: orgLevels.find(o => o.id === orgLevelId)?.name
            },
            questionnaireKey: props.questionnaireKey
        });
    };
    const questions = Object.values(props.questions || {});

    return (
        <OldProgressBarQuestionnaireSectionUnconnected
            {...props}
            {...sections.PersonalDetails}
        >
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Title)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.title : ""}
                mandatory
                validators={[validators.validLength({ max: 25, message: "Maximum of 25 characters only" })]}
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.FirstName)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.firstName : ""}
                mandatory
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.LastName)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.lastName : ""}
                mandatory
            />
            <DropdownQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Gender)}
                options={GENDER_OPTIONS}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.gender : ""}
                mandatory
            />
            <OldDateQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Dob)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.dateOfBirth?.toString() : ""}
                mandatory
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AddressLine1)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.addressLine1 : ""}
                mandatory
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AddressLine2)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.addressLine2 : ""}
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.AddressLine3)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.addressLine3 : ""}
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.City)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.city : ""}
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.County)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.county : ""}
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.Postcode)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.postCode : ""}
                mandatory
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.MobilePhone)}
                readonly={props.readonly}
                validators={[validators.validPhoneNumber(10)]}
                answer={props.autoFill ? props.employeeRecord?.mobilePhoneNumber : ""}
                mandatory
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.HomePhone)}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.homePhoneNumber : ""}
                validators={[validators.validPhoneNumber(10)]}
            />
            <DropdownQuestion
                search
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OrgLevelId)}
                options={orgLevels.map(o => ({ text: o.name, value: o.id }))}
                hidden={props.readonly}
                readonly={props.readonly}
                answer={props.autoFill ? props.employeeRecord?.orgStructureOne?.id : ""}
                onAnswerChange={onOrgLevelChange}
                mandatory
            />
            <TextQuestion
                readonly
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OrgLevelName)}
                hidden={!props.readonly}
                answer={props.autoFill ? props.employeeRecord?.orgStructureOne?.name : ""}
            />
            {(!props.readonly || checkExistenceOfQuestionWithId(questions, questionIds.StartDateOfEmployment)) &&
                <OldDateQuestion
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.StartDateOfEmployment)}
                    readonly={props.readonly}
                    mandatory
                />
            }
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.ShiftPattern)}
                readonly={props.readonly}
                mandatory
            />
            <TextQuestion
                questionConfig={getQuestionConfig(props.questionConfigs, questionIds.StandardHours)}
                readonly={props.readonly}
                mandatory
            />
        </OldProgressBarQuestionnaireSectionUnconnected>
    );
};

// eslint-disable-next-line max-len
export const HealthSurveillancePersonalDetailsSection = connect(makeMapStateToQuestionnaireSectionProps(sections.PersonalDetails.id), mapDispatchToQuestionnaireSectionProps)(HealthSurveillancePersonalDetailsSectionUnconnected);
